import axios from 'axios';
import { authService } from './authService';
import config from '../config/environment';

// API User Profile Response Interface
export interface ApiUserProfile {
  userUuid: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: number;
  dob: string | null;
  roles: string;
  createdAt: string;
  updatedAt: string;
}

// Local User Profile Interface (for UI compatibility)
export interface UserProfile {
  userUuid: string;
  firstName: string;
  lastName: string;
  fullName: string; // Computed field
  email: string;
  phone: string; // Convert number to string for UI
  dob: string | null;
  roles: string;
  createdAt: string;
  updatedAt: string;
}

// Create axios instance for user API calls
const userApi = axios.create({
  baseURL: config.authServerUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include access token
userApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
userApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await authService.refreshAccessToken();
        const token = localStorage.getItem('access_token');
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return userApi(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        authService.logoutLocal();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper function to transform API response to UI format
const transformApiUserProfile = (apiProfile: ApiUserProfile): UserProfile => {
  return {
    userUuid: apiProfile.userUuid,
    firstName: apiProfile.firstName,
    lastName: apiProfile.lastName,
    fullName: `${apiProfile.firstName} ${apiProfile.lastName}`.trim(),
    email: apiProfile.email,
    phone: apiProfile.phone.toString(),
    dob: apiProfile.dob,
    roles: apiProfile.roles,
    createdAt: apiProfile.createdAt,
    updatedAt: apiProfile.updatedAt,
  };
};

export const userService = {
  /**
   * Get user profile by UUID
   */
  getUserProfile: async (userUuid?: string): Promise<UserProfile> => {
    try {
      // Use provided userUuid or get from auth service
      const uuid = userUuid || authService.getUserUuid();
      if (!uuid) {
        throw new Error('No user UUID available');
      }
      
      console.log('👤 Fetching user profile for UUID:', uuid);
      
      const response = await userApi.get<ApiUserProfile>(`/api/users/${uuid}`);
      
      console.log('✅ User profile fetched successfully:', response.data);
      
      // Transform API response to UI format
      const userProfile = transformApiUserProfile(response.data);
      
      // Cache the profile locally for offline access
      localStorage.setItem('userProfile', JSON.stringify(userProfile));
      
      return userProfile;
    } catch (error) {
      console.error('❌ Error fetching user profile:', error);
      
      // Try to get cached profile if API fails
      const cachedProfile = localStorage.getItem('userProfile');
      if (cachedProfile) {
        console.log('📦 Using cached user profile');
        return JSON.parse(cachedProfile);
      }
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('User profile not found');
        }
        
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to fetch user profile';
          throw new Error(`Failed to fetch user profile: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to fetch user profile: ${(error as Error).message}`);
    }
  },

  /**
   * Update user profile
   * Note: Email is excluded for security reasons as it's used for login
   */
  updateUserProfile: async (userUuid: string, updates: {
    firstName?: string;
    lastName?: string;
    phone?: string | number;
    dob?: string;
  }): Promise<UserProfile> => {
    try {
      console.log('📝 Updating user profile:', userUuid, updates);
      
      // Prepare API payload - convert phone to number and map dob field
      const apiPayload: any = {};
      
      if (updates.firstName !== undefined) {
        apiPayload.firstName = updates.firstName;
      }
      
      if (updates.lastName !== undefined) {
        apiPayload.lastName = updates.lastName;
      }
      
      if (updates.phone !== undefined) {
        // Convert phone to number for API
        apiPayload.phone = typeof updates.phone === 'string' 
          ? parseInt(updates.phone.replace(/\D/g, ''), 10) 
          : updates.phone;
      }
      
      if (updates.dob !== undefined) {
        apiPayload.dob = updates.dob;
      }
      
      console.log('🚀 Sending API payload:', apiPayload);
      
      const response = await userApi.put<ApiUserProfile>(`/api/users/${userUuid}`, apiPayload);
      
      console.log('✅ User profile updated successfully:', response.data);
      
      const userProfile = transformApiUserProfile(response.data);
      
      // Update cached profile
      localStorage.setItem('userProfile', JSON.stringify(userProfile));
      
      // Dispatch event for components listening to profile updates
      window.dispatchEvent(new CustomEvent('userProfileUpdated', { detail: userProfile }));
      
      return userProfile;
    } catch (error) {
      console.error('❌ Error updating user profile:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error('Invalid profile data. Please check your inputs.');
        }
        
        if (error.response?.status === 404) {
          throw new Error('User profile not found');
        }
        
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to update user profile';
          throw new Error(errorMessage);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to update user profile: ${(error as Error).message}`);
    }
  },

  /**
   * Get cached user profile (for offline/quick access)
   */
  getCachedUserProfile: (): UserProfile | null => {
    try {
      const cachedProfile = localStorage.getItem('userProfile');
      return cachedProfile ? JSON.parse(cachedProfile) : null;
    } catch (error) {
      console.error('Error parsing cached user profile:', error);
      localStorage.removeItem('userProfile');
      return null;
    }
  },

  /**
   * Clear cached user profile
   */
  clearCachedUserProfile: (): void => {
    localStorage.removeItem('userProfile');
  }
};

export default userService;